import { useDebounceState } from "@prismicio/editor-support/React"
import { toURL } from "@prismicio/editor-support/URL"
import {
  Button,
  Field,
  FieldControl,
  FieldFooter,
  FieldIcon,
  FieldLabel,
} from "@prismicio/editor-ui"
import type { EmbedContent } from "@prismicio/types-internal/lib/content"
import type { Embed as EmbedT } from "@prismicio/types-internal/lib/customtypes"
import { startTransition } from "react"

import { FieldTextInput } from "../FieldTextInput"
import { getFieldLabel, getFieldPlaceholder } from "../utils"
import { Footer } from "./EmbedFooter"

interface Props {
  id: string
  readOnly: boolean
  field: EmbedT
  content?: EmbedContent
  onContentChange: (content?: EmbedContent) => void
}

export function EmbedField(props: Props) {
  const { id, readOnly, field, content, onContentChange } = props

  const url = useDebounceState({
    initialState: content?.embed_url ?? "",
    startTransition,
  })

  function onUrlChange(newUrl: string) {
    url.set(newUrl)
    if (!toURL(newUrl)) onContentChange(undefined)
  }

  return (
    <Field
      footer={
        toURL(url.value) && (
          <FieldFooter>
            <Footer
              debouncedUrl={url.debouncedValue}
              content={content}
              onContentChange={onContentChange}
            />
          </FieldFooter>
        )
      }
    >
      <FieldIcon name="cloud" />
      <FieldLabel htmlFor={id}>{getFieldLabel(field)}</FieldLabel>
      <FieldControl>
        <FieldTextInput
          id={id}
          readOnly={readOnly}
          placeholder={getFieldPlaceholder(field)}
          value={url.value}
          onValueChange={onUrlChange}
          variant={toURL(url.value) ? "link" : "normal"}
          endAdornment={
            <Button
              startIcon="close"
              color="grey"
              size="small"
              disabled={!url}
              onClick={() => onUrlChange("")}
            />
          }
        />
      </FieldControl>
    </Field>
  )
}
